<template>
  <div class="p-6 dark:text-gray-50" v-if="canAddOrg || user?.is_staff || (canViewOrg && !isNewOrg)">
    <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-6 flex items-center">
      <button
        @click="$router.push('/organizations')"
        class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4 mr-1"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
            clip-rule="evenodd"
          />
        </svg>
        Back to Organisations
      </button>
      {{ isNewOrg ? "Add Organisation" : "Edit Organisation" }}
      <div class="ml-3 mt-2">
        <div class="relative group">
          <button
            type="button"
            class="hover:bg-transparent bg-transparent"
            @click="startTutorial"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
          </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 glass-card mb-1 px-1.5 py-0.5 text-xs text-white bg-gray-800 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
      </div>
    </h1>

    <div v-if="loading" class="text-center py-4">
      <p class="text-gray-600">Loading...</p>
    </div>

    <div
      v-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
      role="alert"
    >
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <form v-else @submit.prevent="saveOrganization" class="space-y-6 p-6 glass-card">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label for="name" class="block text-sm font-medium text-gray-700 dark:text-white"
            >Name</label
          >
          <input
            type="text"
            id="name"
            v-model="org.name"
            @focus="clearFieldError('name')"
            :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['name']
                  ? 'ring-2 ring-red-500  focus:ring-red-500'
                  : 'focus:ring-blue-500'
            ]"
          />
          <FieldError :message="fieldErrors['name']"/>
        </div>

        <div>
          <label
            for="abbreviated_name"
            class="block text-sm font-medium text-gray-700 dark:text-white"
            >Abbreviated Name</label
          >
          <input
            type="text"
            id="abbreviated_name"
            v-model="org.abbreviated_name"
            @focus="clearFieldError('abbreviated_name')"
            :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['abbreviated_name']
                  ? 'ring-2 ring-red-500  focus:ring-red-500'
                  : 'focus:ring-blue-500'
            ]"
          />
          <FieldError :message="fieldErrors['abbreviated_name']"/>
        </div>

        <div v-if="!isSplitpoint">
          <label
            for="service_provider"
            class="block text-sm font-medium text-gray-700 dark:text-white"
            >Service Provider</label
          >
          <GlassSelect
            id="service_provider"
            v-model="org.service_provider"
            :options="serviceProviders"
            placeholder="Select a service provider"
            :valueKey="'id'"
            :labelKey="'name'"
          />
        </div>

          <div>
            <label for="code" class="block text-sm font-medium text-gray-700 dark:text-white"
              >Code</label
            >
            <input
              type="text"
              id="code"
              v-model="org.code"
              :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['code']
                    ? 'ring-2 ring-red-500  focus:ring-red-500'
                    : 'focus:ring-blue-500'
              ]"
            />
            <FieldError :message="fieldErrors['code']"/>
          </div>

          <div>
            <label
              for="domain_allowlist"
              class="block text-sm font-medium text-gray-700 dark:text-white"
              >Allowed Domains</label
            >
            <textarea
              id="domain_allowlist"
              v-model="domainAllowlistText"
              rows="3"
              placeholder="Enter domains separated by commas"
              :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['domain_allowlist']
                    ? 'ring-2 ring-red-500  focus:ring-red-500'
                    : 'focus:ring-blue-500'
              ]"  
            ></textarea>
            <FieldError :message="fieldErrors['domain_allowlist']"/>
          </div>

          <!-- Alloy Tenant IDs Section -->
          <div class="md:col-span-2 space-y-3 border-t pt-4 mt-2">
            <h3 class="text-lg font-semibold text-gray-700 dark:text-white">
              Alloy Tenant IDs
            </h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Tenant IDs that Alloy attaches to metrics it pushes to Mimir (the X-Scope-OrgID header). Leave blank to inherit from the parent service provider.
            </p>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- SNMP Tenant -->
              <div class="space-y-2">
                <label for="mimir_snmp_tenant" class="block text-sm font-medium text-gray-700 dark:text-white">
                  SNMP Tenant ID
                </label>
                <input
                  type="text"
                  id="mimir_snmp_tenant"
                  v-model="org.mimir_snmp_tenant"
                  placeholder="e.g., snmp-prod-tenant"
                  :class="[
                    'w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2',
                    fieldErrors['mimir_snmp_tenant']
                      ? 'ring-2 ring-red-500 focus:ring-red-500'
                      : 'focus:ring-blue-500'
                  ]"
                />
                <FieldError :message="fieldErrors['mimir_snmp_tenant']"/>
                <label for="mimir_snmp_retention" class="block text-xs font-medium text-gray-600 dark:text-gray-300 pt-1">
                  Retention
                </label>
                <GlassSelect
                  id="mimir_snmp_retention"
                  v-model="org.mimir_snmp_retention"
                  :options="mimirRetentionOptions"
                  valueKey="value"
                  labelKey="label"
                  :error="!!fieldErrors['mimir_snmp_retention']"
                />
                <FieldError :message="fieldErrors['mimir_snmp_retention']"/>
              </div>

              <!-- Endpoint Availability Tenant -->
              <div class="space-y-2">
                <label for="mimir_blackbox_tenant" class="block text-sm font-medium text-gray-700 dark:text-white">
                  Endpoint Availability Tenant ID
                </label>
                <input
                  type="text"
                  id="mimir_blackbox_tenant"
                  v-model="org.mimir_blackbox_tenant"
                  placeholder="e.g., blackbox-prod-tenant"
                  :class="[
                    'w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2',
                    fieldErrors['mimir_blackbox_tenant']
                      ? 'ring-2 ring-red-500 focus:ring-red-500'
                      : 'focus:ring-blue-500'
                  ]"
                />
                <FieldError :message="fieldErrors['mimir_blackbox_tenant']"/>
                <label for="mimir_blackbox_retention" class="block text-xs font-medium text-gray-600 dark:text-gray-300 pt-1">
                  Retention
                </label>
                <GlassSelect
                  id="mimir_blackbox_retention"
                  v-model="org.mimir_blackbox_retention"
                  :options="mimirRetentionOptions"
                  valueKey="value"
                  labelKey="label"
                  :error="!!fieldErrors['mimir_blackbox_retention']"
                />
                <FieldError :message="fieldErrors['mimir_blackbox_retention']"/>
              </div>

              <!-- Top Talkers Tenant -->
              <div class="space-y-2">
                <label for="mimir_darkstat_tenant" class="block text-sm font-medium text-gray-700 dark:text-white">
                  Top Talkers Tenant ID
                </label>
                <input
                  type="text"
                  id="mimir_darkstat_tenant"
                  v-model="org.mimir_darkstat_tenant"
                  placeholder="e.g., darkstat-prod-tenant"
                  :class="[
                    'w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2',
                    fieldErrors['mimir_darkstat_tenant']
                      ? 'ring-2 ring-red-500 focus:ring-red-500'
                      : 'focus:ring-blue-500'
                  ]"
                />
                <FieldError :message="fieldErrors['mimir_darkstat_tenant']"/>
                <label for="mimir_darkstat_retention" class="block text-xs font-medium text-gray-600 dark:text-gray-300 pt-1">
                  Retention
                </label>
                <GlassSelect
                  id="mimir_darkstat_retention"
                  v-model="org.mimir_darkstat_retention"
                  :options="mimirRetentionOptions"
                  valueKey="value"
                  labelKey="label"
                  :error="!!fieldErrors['mimir_darkstat_retention']"
                />
                <FieldError :message="fieldErrors['mimir_darkstat_retention']"/>
              </div>

              <!-- API Tenant -->
              <div class="space-y-2">
                <label for="mimir_fortigate_tenant" class="block text-sm font-medium text-gray-700 dark:text-white">
                  API Tenant ID
                </label>
                <input
                  type="text"
                  id="mimir_fortigate_tenant"
                  v-model="org.mimir_fortigate_tenant"
                  placeholder="e.g., fortigate-prod-tenant"
                  :class="[
                    'w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2',
                    fieldErrors['mimir_fortigate_tenant']
                      ? 'ring-2 ring-red-500 focus:ring-red-500'
                      : 'focus:ring-blue-500'
                  ]"
                />
                <FieldError :message="fieldErrors['mimir_fortigate_tenant']"/>
                <label for="mimir_fortigate_retention" class="block text-xs font-medium text-gray-600 dark:text-gray-300 pt-1">
                  Retention
                </label>
                <GlassSelect
                  id="mimir_fortigate_retention"
                  v-model="org.mimir_fortigate_retention"
                  :options="mimirRetentionOptions"
                  valueKey="value"
                  labelKey="label"
                  :error="!!fieldErrors['mimir_fortigate_retention']"
                />
                <FieldError :message="fieldErrors['mimir_fortigate_retention']"/>
              </div>

              <!-- Mimir Endpoint URL -->
              <div class="col-span-2">
                <label for="mimir_endpoint" class="block text-sm font-medium text-gray-700 dark:text-white">
                  Mimir Endpoint URL
                </label>
                <input
                  id="mimir_endpoint"
                  v-model="org.mimir_endpoint"
                  type="text"
                  placeholder="e.g. http://10.8.8.138:9009/api/v1/push"
                  :class="[
                    'mt-1 block w-full rounded-md shadow-sm sm:text-sm p-2 border dark:bg-gray-700 dark:text-white',
                    fieldErrors['mimir_endpoint']
                      ? 'border-red-500'
                      : 'border-gray-300 dark:border-gray-600',
                  ]"
                />
                <p class="text-xs text-gray-500 mt-1">The Mimir remote write endpoint for all alloy exporters in this organization.</p>
                <FieldError :message="fieldErrors['mimir_endpoint']"/>
              </div>
            </div>
          </div>

          <!-- Default Profiles Section -->
          <div class="my-6 md:col-span-2">
            <h3 class="text-md font-semibold text-gray-700 dark:text-white mb-3">Default Profiles</h3>
            <p class="text-xs text-gray-500 mb-4">Probes in this organisation inherit these profiles unless an explicit profile is set on the probe (or in the probe's observability config).</p>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label for="default_snmp_profile_id" class="block text-sm font-medium text-gray-700 dark:text-white">Default SNMP Profile</label>
                <GlassSelect
                  id="default_snmp_profile_id"
                  v-model="org.default_snmp_profile_id"
                  :options="snmpProfileSelectOptions"
                  valueKey="value"
                  labelKey="label"
                  placeholder="None"
                />
              </div>
              <div>
                <label for="default_wifi_profile_id" class="block text-sm font-medium text-gray-700 dark:text-white">Default WiFi Profile</label>
                <GlassSelect
                  id="default_wifi_profile_id"
                  v-model="org.default_wifi_profile_id"
                  :options="wifiProfileSelectOptions"
                  valueKey="value"
                  labelKey="label"
                  placeholder="None"
                />
              </div>
              <div>
                <label for="default_fortigate_profile_id" class="block text-sm font-medium text-gray-700 dark:text-white">Default Fortigate Profile</label>
                <GlassSelect
                  id="default_fortigate_profile_id"
                  v-model="org.default_fortigate_profile_id"
                  :options="fortigateProfileSelectOptions"
                  valueKey="value"
                  labelKey="label"
                  placeholder="None"
                />
              </div>
            </div>
          </div>

          <div v-if="user?.is_staff" class="flex items-center">
            <input
              type="checkbox"
              id="is_service_provider"
              v-model="org.is_service_provider"
              class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <div class="ml-3 text-sm">
              <label
                for="is_service_provider"
                class="block text-sm text-gray-900 dark:text-white font-medium"
                >Is Service Provider</label
              >
              <p>Designates this organisation as a service provider to other organisations</p>
            </div>
          </div>

        <div class="space-y-2">
          <div v-if="!isSplitpoint && (canViewHost || user?.is_staff) && org?.id">
            <h1 class="block text-sm font-medium text-gray-700 dark:text-white">Important Hosts</h1>
            <div @mouseenter="isdropDownVisible = true, hoveritem = important_hosts"
                @mouseleave="isdropDownVisible = false"
                class="mt-1 relative"
            >
              <div class="w-full text-gray-700 font-medium bg-gray-200 hover:bg-gray-300 pl-4 py-1.5 text-center inline-flex dark:bg-gray-700 dark:text-gray-50 dark:hover:text-gray-900 rounded cursor-pointer">
                {{ 'Important Hosts' }}
                <svg
                  class="ml-auto h-5 w-5 transform transition-transform duration-500 ease-in-out"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  :class="{'rotate-180': isdropDownVisible }"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
              
              <!-- CSS-only animated dropdown -->
              <ul class="absolute top-full left-0 w-full bg-gray-200 dark:bg-gray-700 shadow-lg overflow-hidden rounded-b z-10 max-h-60 overflow-y-auto transition-all duration-500 ease-out origin-top"
                  :class="{
                    'opacity-100 scale-y-100 translate-y-0': isdropDownVisible,
                    'opacity-0 scale-y-0 -translate-y-4 pointer-events-none': !isdropDownVisible
                  }">
                <li 
                  v-for="host in important_hosts"
                  :key="host.id"
                  class="px-4 py-3 font-medium cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200 ease-in-out"
                  @click="editHost(host.id)"
                > 
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-50 transition-colors duration-200">
                    {{ host.destination }} 
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div 
            v-show="newOrgHosts"
            class="grid grid-cols-4 mt-2 gap-2 flex-1"
            >
            <div
              v-for="(item, index) in newOrgHosts" :key="index"
              class="bg-gray-300 dark:bg-gray-600 rounded p-1 min-w-32 text-center mb-2 flex justify-between px-2">
              <p class="truncate">{{ item.destination }}</p>
              <button 
                type="button"
                @click="removeNewOrgOpt(index, newOrgHosts)"
                class="text-gray-500 hover:text-gray-700 dark:hover:text-white dark:text-gray-50 focus:outline-none"
                >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
          </div>

          <button v-if="!isSplitpoint && (canAddHost || user?.is_staff) && org?.id"
              type="button"
              @click="addHost"
              class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Add Important Host
          </button>
        </div>

      </div>

      <div>
        <Teleport to="body">
        <div v-if="addingImportantHost || updatingImportantHost"
          class="flex items-center justify-center inset-0 fixed bg-black/50 backdrop-blur-sm z-40"
        >
          <div
            class="p-6 glass-card mb-2 w-[80%] min-h-[35%] shadow-xl z-50">
            <div class="flex grid grid-cols-1 md:grid-cols-2 gap-6">

              <div class="flex flex-col">
                <label for="description" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Description<span class="text-red-500">*</span>:</label>
                <input 
                  v-model="host.description"
                  id="description"
                  required
                  class="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g Host"
                />
              </div>

              <div class="flex flex-col">
                <label for="description" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Destination<span class="text-red-500">*</span>:</label>
                <input 
                  v-model="host.destination"
                  id="destination"
                  required
                  placeholder="e.g 11.2.10.20 or https://splitpoint.org.co.za "
                  :class="['border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['destination']
                        ? 'ring-2 ring-red-500  focus:ring-red-500'
                        : 'focus:ring-blue-500'
                  ]"
                />
                <FieldError :message="fieldErrors['destination']"/>
              </div>

              <div class="flex flex-col">
                <label for="region" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Protocol:<span class="text-red-500">*</span>:</label>
                <GlassSelect
                  v-model="host.protocol"
                  id="protocol"
                  :options="protocolItems"
                  placeholder="Protocol"
                />
              </div>
                        
              <div class="flex flex-col">
                <label for="port" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Port:<span class="text-red-500">*</span>:</label>
                <input 
                  v-model="host.port"
                  id="port"
                  required 
                  min="1"
                  max="65535"
                  placeholder="8080"
                  :class="['border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['port']
                        ? 'ring-2 ring-red-500  focus:ring-red-500'
                        : 'focus:ring-blue-500'
                  ]"
                />
                <FieldError :message="fieldErrors['port']"/>
              </div>
                    
              <div class="flex flex-col">
                <label for="timeout" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Timeout:<span class="text-red-500">*</span>:</label>
                <input 
                  v-model="host.timeout"
                  id="timeout"
                  required 
                  min="1"
                  placeholder="e.g 30"
                  :class="['border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['timeout']
                        ? 'ring-2 ring-red-500  focus:ring-red-500'
                        : 'focus:ring-blue-500'
                  ]" 
                />
                <FieldError :message="fieldErrors['timeout']"/>
              </div>
                    
              <div class="flex justify-between">
                <div>
                  <button v-if="canChangeHost || user?.is_staff"
                    type="button"
                    @click="manageHost"
                    class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                    >
                    Save
                  </button>
                  <button
                    type="button"
                    @click="cancelHost"
                    class="ml-2 px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
                  >
                    Cancel
                  </button>
                </div>
                <div v-if="!addingImportantHost && ( canDeleteHost || user?.is_staff)">
                  <button
                    type="button"
                    @click="deleteSelectedHost()"
                    class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-200"
                    >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        </Teleport>
      </div>

      <!-- SNMP Auth Profiles (org-level) -->
      <div v-if="org.id && permsLoaded">
        <SnmpAuthProfilesSection :schema-name="schemaName" />
      </div>

      <!-- SNMP Configurations -->
      <div v-if="org.id && permsLoaded">
        <SnmpTab
          parent-type="org"
          :parent-id="org.id"
          :org-id="org.id"
          :schema-name="schemaName"
        />
      </div>

      <div class="sticky bottom-0 z-20 -mx-6 -mb-6 px-6 py-4 flex justify-end space-x-3 bg-white/90 dark:bg-surface-dark/90 backdrop-blur-md rounded-b-2xl shadow-[0_-4px_12px_-6px_rgba(0,0,0,0.08)]">
        <button
          type="button"
          @click="$router.push({ name: 'Organizations' })"
          class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </button>
        <button v-if="(canChangeOrg || user?.is_staff) || (isNewOrg && (canAddOrg || user?.is_staff))"
          id="submit-org-btn"
          type="submit"
          class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {{ isNewOrg ? "Create Organisation" : "Save Changes" }}
        </button>
      </div>
    </form>
  </div>

  <!-- Error Message -->
  <PageError v-if="permsLoaded && !canAddOrg && !user?.is_staff && !(canViewOrg && !isNewOrg)"
    :error="errorMessages['403']"
    :errorCode="'403'"
  />
</template>

<script>
import {
  getOrgById,
  createOrg,
  updateOrg,
  getAllOrgs,
} from "@/services/orgService";
import { getAllHosts, createHost, updateHost, deleteHost } from "@/services/hostService";
import { getAllSnmps, updateSNMP , createSNMP, deleteSNMP } from "@/services/snmpService";
import { getAllSnmpWalks, updateSnmpWalk, createSnmpWalk, deleteSnmpWalk } from "@/services/snmpwalkoidService";
import { useStore } from "@/store";
import FieldError from "@/components/FieldError.vue";
import PageError from "@/components/PageError.vue";
import { errorMessages } from "@/utils/errorMessages";
import SnmpTab from "@/components/SiteDetail/SnmpTab.vue";
import SnmpAuthProfilesSection from "@/components/SnmpAuthProfilesSection.vue";
import { useTutorial } from "@/composables/tutorial";
import { useMultiTenant } from "@/composables/useMultiTenant";
import GlassSelect from "@/components/GlassSelect.vue";
import { getAllSNMPProfiles } from "@/services/snmpProfileService";
import { getAllWiFiProfiles } from "@/services/wifiProfileService";
import { getAllFortigateProfiles } from "@/services/fortigateProfileService";

export default {
  name: "OrganizationDetails",
  components: {
    FieldError,
    PageError,
    SnmpTab,
    SnmpAuthProfilesSection,
    GlassSelect,
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  data() {
    return {
      // Objects
      errorMessages: errorMessages,
      org: {
        name: "",
        abbreviated_name: "",
        is_service_provider: false,
        domain_allowlist: [],
        code: null,
        service_provider: null,
        // Mimir tenant fields
        mimir_snmp_tenant: "",
        mimir_blackbox_tenant: "",
        mimir_darkstat_tenant: "",
        mimir_fortigate_tenant: "",
        mimir_endpoint: "",
        // Mimir per-tenant retention overrides (default 7 days)
        mimir_snmp_retention: "7d",
        mimir_blackbox_retention: "7d",
        mimir_darkstat_retention: "7d",
        mimir_fortigate_retention: "7d",
        // Default profile FKs (apply org-wide; probe-level assignments override)
        default_snmp_profile_id: null,
        default_wifi_profile_id: null,
        default_fortigate_profile_id: null,
      },
      mimirRetentionOptions: [
        { value: "7d",   label: "7 days" },
        { value: "30d",  label: "30 days" },
        { value: "90d",  label: "90 days" },
        { value: "180d", label: "180 days" },
        { value: "365d", label: "1 year" },
        { value: "730d", label: "2 years" },
      ],
      snmpProfileOptions: [],
      wifiProfileOptions: [],
      fortigateProfileOptions: [],
      host: this.defaultHost(),

      // Data
      protocolItems: ['TCP', 'UDP'],
      domainAllowlistText: "",
      loading: false,
      error: null,
      isNewOrg: false,
      serviceProviders: [],
      important_hosts: [],
      walkOIDs: [],
      isdropDownVisible: false,
      addingImportantHost: false,
      updatingImportantHost : false,
      selectedHost: null,
      newOrgHosts: [],
      fieldErrors: {},


      orgDetailTutorialSteps: [
        {
          element: '#name',
          action: 'type',
          value: 'Tutorial Organisation',
          message: 'Enter the full name of the organisation.'
        },
        {
          element: '#abbreviated_name',
          action: 'type',
          value: 'TO',
          message: 'Enter a short abbreviated name.'
        },
        {
          element: '#code',
          action: 'type',
          value: 'TO001',
          message: 'Enter a unique code for this organisation.'
        },
        {
          element: '#service_provider',
          action: 'select',
          value: -1,
          message: 'Select the service provider for this organisation.'
        },
        {
          element: '#submit-org-btn',
          action: 'display',
          message: 'Click to create or save the organisation.'
        },
      ],

      //Permissions
      canChangeOrg:false,
      canAddOrg: false,
      canViewOrg: false,
      canChangeHost: false,
      canViewHost: false,
      canDeleteHost: false,
      canAddHost: false,
      
    };
  },
  computed: {
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    orgId() {
      return this.org?.id || null;
    },
    isSplitpoint() {
      return this.org.name?.toLowerCase() === 'splitpoint';
    },
    user() {
      return useStore.getUser();
    }, 
    formattedOrg() {
      return {
        ...this.org,
        domain_allowlist: this.domainAllowlistText
          .split(",")
          .map((domain) => domain.trim())
          .filter(Boolean),
        last_modified: new Date().toISOString(),
        created_date: this.org.created_date || new Date().toISOString(),
      };
    },
    schemaName() {
      return this.org?.schema_name || null;
    },
    snmpProfileSelectOptions() {
      return [
        { value: null, label: "None" },
        ...this.snmpProfileOptions.map(p => ({ value: p.id, label: p.name })),
      ];
    },
    wifiProfileSelectOptions() {
      return [
        { value: null, label: "None" },
        ...this.wifiProfileOptions.map(p => ({ value: p.id, label: p.name })),
      ];
    },
    fortigateProfileSelectOptions() {
      return [
        { value: null, label: "None" },
        ...this.fortigateProfileOptions.map(p => ({ value: p.id, label: p.name })),
      ];
    },
  },
  watch: {
    user: {
      async handler(user) {
        if (user) {
          await this.checkPermissions();
          if (!this.isNewOrg && this.org?.id && (this.canViewHost || user?.is_staff)) {
            await this.fetchHosts();
          }
        }
      },
      immediate: true
    },
  },
  async created() {
    const orgId = this.$route.params.id;
    if (orgId === "new") {
      this.isNewOrg = true;
    } else {
      await this.fetchOrganization(orgId);
      await this.checkPermissions();
      if (this.canViewHost || this.user?.is_staff) await this.fetchHosts();
    }
    await this.fetchServiceProviders();
    await this.fetchProfileOptions();
  },
  methods: {
    async fetchProfileOptions() {
      const schema = this.org?.schema_name || null;
      const unwrap = (r) => Array.isArray(r) ? r : (r?.results || []);
      try {
        const snmp = await getAllSNMPProfiles(schema);
        this.snmpProfileOptions = unwrap(snmp).map(p => ({ id: p.id, name: p.name }));
      } catch (e) { console.error("SNMP profile fetch:", e); }
      try {
        const wifi = await getAllWiFiProfiles(schema);
        this.wifiProfileOptions = unwrap(wifi).map(p => ({ id: p.id, name: p.name }));
      } catch (e) { console.error("WiFi profile fetch:", e); }
      try {
        const fg = await getAllFortigateProfiles(schema);
        this.fortigateProfileOptions = unwrap(fg).map(p => ({ id: p.id, name: p.name }));
      } catch (e) { console.error("Fortigate profile fetch:", e); }
    },
    clearFieldError(fieldName){
      if (this.fieldErrors[fieldName]) {
        delete this.fieldErrors[fieldName];
        delete this.fieldErrors['non_field_errors'];
      }
    },
    validateOrgData(){
      this.fieldErrors = {};
      if (this.formattedOrg.name === ""){
        this.fieldErrors['name'] = "Please specify a name for this organisation"
      }
      if (this.formattedOrg.abbreviated_name === ""){
        this.fieldErrors['abbreviated_name'] = "Please specify an abbreviated name for this organisation"
      }
    },

    async startTutorial() {
      const originalServiceProviders = [...this.serviceProviders];
      const originalOrg = { ...this.org };

      this.serviceProviders = [{ id: -1, name: 'Tutorial Organisation' }];

      await this._start(this.orgDetailTutorialSteps, () => {
        this.serviceProviders = originalServiceProviders;
        this.org = { ...originalOrg };
      });
    },
    async checkPermissions() {
      if (this.user) {
     
        this.canChangeOrg = await useStore.hasPermission("Can change organisation");
        this.canAddOrg = await useStore.hasPermission("Can add organisation");
        this.canViewOrg = await useStore.hasPermission("Can view organisation");

        this.canViewHost = await useStore.hasPermission("Can view host");
        this.canDeleteHost = await useStore.hasPermission("Can delete host");
        this.canChangeHost = await useStore.hasPermission("Can change host");
        this.canAddHost = await useStore.hasPermission("Can add host");
      }
    },
    async fetchOrganization(orgId) {
      this.loading = true;
      this.error = null;
      try {
        this.org = await getOrgById(orgId);
        if (this.org?.domain_allowlist) this.domainAllowlistText = this.org.domain_allowlist.join(", ");
      } catch (error) {
        console.error("Error fetching organization:", error);
        this.error =
          "An error occurred while fetching organization details. Please try again later.";
      } finally {
        this.loading = false;
      }
    },
    async fetchServiceProviders() {
      try {
        const allOrgs = await getAllOrgs();
        this.serviceProviders = allOrgs.filter(
          (org) => org.is_service_provider
        );
      } catch (error) {
        console.error("Error fetching service providers:", error);
        this.error =
          "An error occurred while fetching service providers. Please try again later.";
      }
    },
    async fetchHosts() {
      try {
        const hosts = await getAllHosts(this.schemaName);
        const org_hosts = hosts.filter(host => host.org === this.org.id);
        this.important_hosts = org_hosts.map((host)=> (
          { ...host, destination: host.destination.replace(/^https?:\/\//, '')}
        ))
      } catch (error) {
        console.error("Error fetching hosts:", error);
      }
    },
    async saveOrganization() {
      this.loading = true;
      this.error = null;
      this.fieldErrors = {};
      
      try {
        const orgData = this.formattedOrg;

        this.validateOrgData()
         if (Object.keys(this.fieldErrors).length > 0) return;

        if (this.isNewOrg) {
          const response = await createOrg(orgData);
          // Create org first and then create the snmp and important hosts
          if(response){
            if(this.newOrgHosts) this.newOrgHosts = this.newOrgHosts.map(item => ({
              ...item,
              org: response.id,
            }));
            await Promise.all(this.newOrgHosts.map(host => createHost(host, this.schemaName)));
          }
        } else {
          await updateOrg(this.org.id, orgData);
        }
        await this.tenant.fetchOrganizations();
        this.$router.push({ name: "Organizations" });
      } catch (error) {
        this.fieldErrors = {};
        const errData = error?.response?.data;
        if (errData) {
          for (const key in errData) {
            const value = errData[key];

            if (Array.isArray(value)) {
              this.fieldErrors[key] = value[0];
            } 
            else if (typeof value === 'object' && value !== null) {
              const nestedFirstKey = Object.keys(value)[0];
              const nestedMessages = value[nestedFirstKey];

              if (Array.isArray(nestedMessages)) {
                this.fieldErrors[key] = nestedMessages[0];
              }
            }
          }
        }

        console.error("Error saving organization:", error);
      } finally {
        this.loading = false;
      }
    },
    defaultHost(){
      return {
        description: "",
        destination: "",
        port: "",
        protocol: "",
        timeout: "",
        probe: null,
        org: null,
        id: null,
        created_date: "",
        last_modified: ""
      }
    },
    async manageHost(){
      try{
        this.fieldErrors = {};
        const formattedHost = {
          ...this.host,
          destination: this.host.destination.startsWith('http') 
            ? this.host.destination 
            : `http://${this.host.destination}` 
        };
        if(this.isNewOrg){
          if(this.host?.destination && this.host?.timeout &&
            this.host?.port
          ){
            this.newOrgHosts.push(formattedHost);
          }
        }
        else{
          if(formattedHost?.id){
            await updateHost(formattedHost.id, formattedHost, this.schemaName);
          }
          else{
            await createHost(formattedHost, this.schemaName);
          }
          this.fetchHosts();
        }  
        this.cancelHost();
      }
      catch(error){

        const errData = error?.response?.data;
        for (const element in  errData){
          const fieldError = errData?.[element];
          if (fieldError?.[0]) {
            this.fieldErrors[element] = fieldError[0]; 
          }
        }
        console.error("Error saving organization:", error);
      }
    },
    editHost(hostID){
      this.updatingImportantHost = true;
      this.selectedHost = this.important_hosts.find(item => item.id === hostID)
      this.isdropDownVisible = false;
      Object.assign(this.host, this.selectedHost);
    },
    addHost(){
      this.addingImportantHost = true;
      this.host.org = this.$route.params.id;
    },
    cancelHost(){
      Object.assign(this.host, this.defaultHost());
      this.addingImportantHost = false;
      this.updatingImportantHost = false;
      this.fieldErrors = {};
    },
    async deleteSelectedHost(){
      try {
        const response = await deleteHost(this.host.id,this.schemaName);
        this.fetchHosts();
        this.cancelHost();
      } catch (error) {
        console.error("Error deleting host:", error);
        this.error =
          "An error occurred while deleting host. Please try again later.";
      }
    },

    
    removeNewOrgOpt(index,array){
      array.splice(index,1);
    },
  },
};
</script>